import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageHero } from "@/components/site/Layout";
import { MapPin, Mail, Phone } from "lucide-react";
import { REGISTER_URL } from "@/components/site/data";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/contact")({
  component: Contact,
  head: () => ({
    meta: [
      { title: "Contact — Arab Capital Markets Conference" },
      { name: "description", content: "Get in touch with the conference organizers." },
      { property: "og:title", content: "Contact the Organizers" },
      {
        property: "og:description",
        content: "Reach out for delegate, sponsorship or media inquiries.",
      },
    ],
  }),
});

const contactSchema = z.object({
  fullName: z.string().min(1, "Full Name is required"),
  email: z.string().email("Invalid email address"),
  designation: z.string().optional(),
  company: z.string().optional(),
  phoneNumber: z.string().optional(),
  country: z.string().optional(),
  message: z.string().min(1, "Message is required"),
});

type ContactValues = z.infer<typeof contactSchema>;

function Contact() {
  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset,
  } = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactValues) => {
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (result.success) {
        toast.success("Thanks — we'll be in touch shortly.");
        reset();
      } else {
        toast.error(result.error || "Something went wrong.");
      }
    } catch {
      toast.error("Network error — please try again.");
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Get in Touch"
        title="Contact Us"
        subtitle="For delegate, sponsorship or media inquiries"
      />
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-8 md:gap-10">
          <div className="md:col-span-2 space-y-8">
            {[
              { title: "To Be a Speaker" },
              { title: "To Sponsor or Exhibit" },
              { title: "To Register" },
            ].map(({ title }) => (
              <div key={title} className="space-y-3">
                <h3 className="font-semibold text-lg">{title}</h3>
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex items-start gap-2 text-foreground">
                    <MapPin className="mt-0.5 shrink-0 text-accent" size={15} />
                    <span>
                      Union of Arab Securities Authorities
                      <br />
                      <span className="text-muted-foreground">Dubai, United Arab Emirates</span>
                    </span>
                  </div>
                  <a
                    href="tel:+97142900056"
                    className="flex items-center gap-2 text-foreground hover:text-accent"
                  >
                    <Phone className="shrink-0 text-accent" size={15} />
                    +971 4 290 0056
                  </a>
                  <a
                    href="mailto:info@uasa.ae"
                    className="flex items-center gap-2 text-foreground hover:text-accent break-all"
                  >
                    <Mail className="shrink-0 text-accent" size={15} />
                    info@uasa.ae
                  </a>
                </div>
              </div>
            ))}
          </div>
          <form
            className="md:col-span-3 bg-card border border-border rounded-xl p-5 sm:p-6 md:p-8 space-y-4 shadow-sm"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input id="fullName" {...register("fullName")} disabled={isSubmitting} />
                {errors.fullName && <p className="text-xs text-destructive">{errors.fullName.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input id="email" type="email" {...register("email")} disabled={isSubmitting} />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="designation">Designation</Label>
                <Input id="designation" {...register("designation")} disabled={isSubmitting} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input id="company" {...register("company")} disabled={isSubmitting} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input id="phoneNumber" {...register("phoneNumber")} disabled={isSubmitting} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input id="country" {...register("country")} disabled={isSubmitting} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Your Message</Label>
              <Textarea id="message" rows={5} {...register("message")} disabled={isSubmitting} />
              {errors.message && <p className="text-xs text-destructive">{errors.message.message}</p>}
            </div>
            <Button
              type="submit"
              className="w-full sm:w-auto bg-primary hover:bg-primary/90"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Contact Us"}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
}
